import { Link, useLocation } from 'react-router-dom';

import { Button, Image } from '@nextui-org/react';
import React from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import AirlockLogo from '../assets/airlock_partner_logo.png';

interface SidebarButtonProps {
  to: string;
  children: React.ReactNode;
  isActive: boolean;
  icon: React.ReactNode;
}

const SidebarButton = ({ to, children, isActive, icon }: SidebarButtonProps) => (
  <Button
    as={Link}
    to={to}
    className={`w-full justify-start text-sm font-medium gap-3 h-10 px-3 ${
      isActive
        ? 'bg-airlock-light text-airlock-purple font-semibold'
        : 'text-slate-600 hover:bg-slate-100'
    }`}
    variant="light"
    radius="sm"
  >
    <span className="text-lg">{icon}</span>
    {children}
  </Button>
);

const OrdersIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
    <line x1="3" y1="6" x2="21" y2="6" />
    <path d="M16 10a4 4 0 01-8 0" />
  </svg>
);

const ResourcesIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
    <polyline points="14,2 14,8 20,8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
  </svg>
);

const ContactIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

const AdminIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z" />
  </svg>
);

const SignOutIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
    <polyline points="16,17 21,12 16,7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

interface SidebarProps {
  onClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ onClose }) => {
  const location = useLocation();
  const { user, logout } = useAuth0();
  const isAdmin =
    user && (user['https://api.airlock/roles'] || []).includes('admin');

  return (
    <div className="flex flex-col h-full">
      {/* Logo — only on desktop (mobile drawer has its own header) */}
      {!onClose && (
        <div className="px-3 pb-5 mb-2 border-b border-slate-100">
          <Image
            src={AirlockLogo}
            alt="AIRLOCK Authorized Partner Program"
            radius="none"
            width={180}
            className="object-contain"
          />
        </div>
      )}

      {/* Navigation */}
      <div className="mb-2 px-3 pt-2">
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
          Navigation
        </p>
      </div>
      <nav className="space-y-1">
        <SidebarButton
          to="/orders"
          isActive={location.pathname === '/orders'}
          icon={<OrdersIcon />}
        >
          Dashboard
        </SidebarButton>
        <SidebarButton
          to="/resources"
          isActive={location.pathname === '/resources'}
          icon={<ResourcesIcon />}
        >
          Resources
        </SidebarButton>
        <SidebarButton
          to="/contact"
          isActive={location.pathname === '/contact'}
          icon={<ContactIcon />}
        >
          Support
        </SidebarButton>
      </nav>

      {isAdmin && (
        <>
          <div className="mt-8 mb-2 px-3">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
              Administration
            </p>
          </div>
          <nav className="space-y-1">
            <SidebarButton
              to="/admin"
              isActive={location.pathname === '/admin'}
              icon={<AdminIcon />}
            >
              Partner Management
            </SidebarButton>
          </nav>
        </>
      )}

      {/* User + Sign Out */}
      <div className="mt-auto pt-4 px-3 border-t border-slate-200">
        <p className="text-xs text-slate-500 truncate mb-3">{user?.email}</p>
        <Button
          variant="light"
          className="w-full justify-start text-sm font-medium gap-3 h-10 px-3 text-slate-500 hover:text-red-500 hover:bg-red-50"
          radius="sm"
          onClick={() =>
            logout({ logoutParams: { returnTo: window.location.origin } })
          }
        >
          <span className="text-lg"><SignOutIcon /></span>
          Sign Out
        </Button>
      </div>
    </div>
  );
};

export default Sidebar;
